import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_BAND_LOCATION } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IBandInitialMethods extends IPluginMethodMap {
  getBandInitialProps(): Promise<TruckLocation[]>;
}

interface TruckLocation {
  band: "TRIBE" | "BLISS" | "HARTS" | "LOST";
  type: "01" | "02";
  latitude: number;
  longitude: number;
}

export class BandInitialService implements IAgentPlugin {
  readonly methods: IBandInitialMethods;

  constructor() {
    this.methods = {
      getBandInitialProps: this.getBandInitialProps.bind(this),
    };
  }

  async getBandInitialProps(): Promise<TruckLocation[]> {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    if (!client) return [];

    try {
      const res = await client.query({
        query: GET_BAND_LOCATION,
        variables: { filter: {} },
        fetchPolicy: "network-only",
      });

      const raw = res?.data?.getLatestBandRoute;

      if (!Array.isArray(raw)) return [];

      const finalResult: TruckLocation[] = [];

      raw.forEach((bandItem: any) => {
        const rawName = bandItem?.bandShortName || bandItem?.name;

        if (!rawName) return;

        const band = this.normalizeBandName(rawName);

        if (!band) return;

        if (
          bandItem?.l1?.coordinates &&
          Array.isArray(bandItem.l1.coordinates) &&
          bandItem.l1.coordinates.length === 2
        ) {
          const [lng, lat] = bandItem.l1.coordinates;

          if (this.isValidCoordinate(lat, lng)) {
            finalResult.push({
              band,
              type: "01",
              latitude: lat,
              longitude: lng,
            });
          }
        }

        if (
          bandItem?.l2?.coordinates &&
          Array.isArray(bandItem.l2.coordinates) &&
          bandItem.l2.coordinates.length === 2
        ) {
          const [lng, lat] = bandItem.l2.coordinates;

          if (this.isValidCoordinate(lat, lng)) {
            finalResult.push({
              band,
              type: "02",
              latitude: lat,
              longitude: lng,
            });
          }
        }
      });

      return finalResult;
    } catch (err) {
      console.error("Error fetching initial band data", err);
      return [];
    }
  }

  private normalizeBandName(
    name: string,
  ): "TRIBE" | "BLISS" | "HARTS" | "LOST" | null {
    const upper = name.toUpperCase().trim();

    if (upper === "TRIBE") return "TRIBE";
    if (upper === "BLISS") return "BLISS";
    if (upper === "HARTS") return "HARTS";

    if (upper === "THE LOST TRIBE" || upper === "LOST TRIBE") return "LOST";

    return null;
  }

  private isValidCoordinate(lat: number, lng: number) {
    return (
      typeof lat === "number" &&
      typeof lng === "number" &&
      lat !== 0 &&
      lng !== 0 &&
      !isNaN(lat) &&
      !isNaN(lng)
    );
  }
}
